import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';

import { Logo } from '../Logo';

import type { CoBrandingLogoSetup } from '../../../config/FeatureFlagSchema';
import type { ComponentProps } from 'react';

export enum CoBrandingLogoVariant {
	White = 'White',
	Color = 'Color',
}

const AdjustedLogoImage = styled.img`
	translate: var(--co-branding-logo-horizontal-offset) var(--co-branding-logo-vertical-offset);
	scale: var(--co-branding-logo-zoom);
`;

interface GetCoBrandingLogoPathOrThrowArgs {
	readonly setup: CoBrandingLogoSetup;
	readonly variant: CoBrandingLogoVariant;
}

function getCoBrandingLogoPathOrThrow({ setup, variant }: GetCoBrandingLogoPathOrThrowArgs): string {
	const path = variant === CoBrandingLogoVariant.Color ? setup.colorLogoPath : setup.whiteLogoPath;

	if (!path) {
		throw new Error(`CoBrandingLogo requires a non-empty ${variant} logo path`);
	}

	return path;
}

export interface CoBrandingLogoProps extends ComponentProps<typeof Logo> {
	readonly setup: CoBrandingLogoSetup;
	readonly variant?: CoBrandingLogoVariant;
}

export const CoBrandingLogo = observer(function CoBrandingLogo({
	setup,
	variant = CoBrandingLogoVariant.White,
	...props
}: CoBrandingLogoProps) {
	const source = getCoBrandingLogoPathOrThrow({ setup, variant });

	const image = (
		<AdjustedLogoImage
			{...{
				src: source,
				alt: 'Organization logo',
				style: {
					'--co-branding-logo-zoom': setup.zoom,
					'--co-branding-logo-horizontal-offset': `${setup.horizontalOffsetInPx}px`,
					'--co-branding-logo-vertical-offset': `${setup.verticalOffsetInPx}px`,
				},
			}}
		/>
	);

	return (
		<Logo
			{...{
				...props,
				children: image,
			}}
		/>
	);
});
